import { useState, useMemo, lazy, Suspense, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { useSubscription } from '@/hooks/useSubscription';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, 
  TrendingDown, 
  Wallet, 
  PiggyBank,
  CreditCard,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';
import { MetricCard } from '@/components/dashboard/MetricCard';
import { FocoScoreCard } from '@/components/dashboard/FocoScoreCard';
import { BudgetRuleGuide } from '@/components/dashboard/BudgetRuleGuide';
import { RecommendationsCard } from '@/components/dashboard/RecommendationsCard';
import { QuickStatsBar } from '@/components/dashboard/QuickStatsBar';
import { RecentActivity } from '@/components/dashboard/RecentActivity';
import { BudgetProgressCard } from '@/components/budget/BudgetProgressCard';
import { BudgetAlertBanner } from '@/components/budget/BudgetAlertBanner';
import { DailyTipProvider } from '@/components/dashboard/DailyTipToast';
import BankStatementImport from '@/components/import/BankStatementImport';
import { PwaInstallBanner } from '@/components/pwa/PwaInstallBanner';
import { PwaInstallNavButton } from '@/components/pwa/PwaInstallNavButton';
import { UserInfoReminder } from '@/components/onboarding/UserInfoReminder';
import { useMonthlySummary } from '@/hooks/useMonthlySummary';
import { useBudgetAlerts } from '@/hooks/useBudgetAlerts';
import { useProfile } from '@/hooks/useProfile';
import { useTransactions } from '@/hooks/useTransactions';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  formatCurrency, 
  formatPercentage,
  getSavingsTrafficLight,
  getDebtTrafficLight,
  getCombinedExpensesTrafficLight,
  getCashFlowTrafficLight,
  generateRecommendations,
} from '@/lib/foco-calculations';

// Lazy load heavy chart components
const DistributionChart = lazy(() => 
  import('@/components/dashboard/DistributionChart').then(m => ({ default: m.DistributionChart }))
);
const IncomeVsExpensesChart = lazy(() => 
  import('@/components/dashboard/IncomeVsExpensesChart').then(m => ({ default: m.IncomeVsExpensesChart }))
);

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

function ChartSkeleton() {
  return (
    <div className="h-[250px] sm:h-[300px] flex items-center justify-center">
      <Skeleton className="w-full h-full rounded-lg" />
    </div>
  );
}

export default function Dashboard() {
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState<'left' | 'right'>('right');
  const contentRef = useRef<HTMLDivElement>(null);
  const signupVerifiedToastShown = useRef(false);
  const checkoutSuccessToastShown = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPremium, waitForPremiumActivation } = useSubscription();

  useEffect(() => {
    if (checkoutSuccessToastShown.current) return;
    if (searchParams.get('checkout') !== 'success') return;

    checkoutSuccessToastShown.current = true;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('checkout');
    setSearchParams(nextParams, { replace: true });

    void (async () => {
      const activated = await waitForPremiumActivation();
      if (activated) {
        toast({
          title: '¡Bienvenido a Premium!',
          description: 'Tu suscripción está activa. Disfruta todas las funciones.',
        });
      } else {
        toast({
          title: 'Pago recibido',
          description:
            'Estamos activando tu plan Premium. Si no ves los cambios en unos segundos, recarga la página.',
        });
      }
    })();
  }, [searchParams, setSearchParams, waitForPremiumActivation]);

  useEffect(() => {
    if (signupVerifiedToastShown.current) return;
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const params = new URLSearchParams(hash);
    if (params.get('type') === 'signup' && params.get('access_token')) {
      signupVerifiedToastShown.current = true;
      toast({
        title: 'Email verificado',
        description: 'Tu cuenta está activa. ¡Bienvenido a FOCO!',
      });
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, []);

  // Get previous month for trend calculation
  const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
  const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;

  const { summary, hasActiveGoals, isLoading, activeGoalsWithProgress } = useMonthlySummary(selectedYear, selectedMonth);
  const { summary: prevSummary } = useMonthlySummary(prevYear, prevMonth);
  const { alerts, warningAlerts, exceededAlerts, hasAlerts } = useBudgetAlerts(selectedMonth, selectedYear);
  const { profile } = useProfile();
  const { transactions } = useTransactions();
  const currency = profile?.default_currency || 'CRC';

  // Calculate trend for a metric
  const calculateTrend = (current: number, previous: number | undefined, isPositive = true) => {
    if (!previous || previous === 0) return undefined;
    const percentage = ((current - previous) / previous) * 100;
    if (Math.abs(percentage) < 1) return { direction: 'neutral' as const, percentage: 0, isPositive };
    return {
      direction: percentage > 0 ? 'up' as const : 'down' as const,
      percentage,
      isPositive
    };
  };

  // Trigger transition animation when month changes
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isTransitioning]);

  // Filter transactions for current month
  const monthlyTransactions = useMemo(() => {
    return transactions?.filter(tx => {
      const txDate = new Date(tx.date);
      return txDate.getMonth() + 1 === selectedMonth && txDate.getFullYear() === selectedYear;
    }) || [];
  }, [transactions, selectedMonth, selectedYear]);

  // Quick stats for the bar
  const quickStats = useMemo(() => [
    { 
      label: 'Tasa de ahorro', 
      current: summary?.savings_rate || 0, 
      format: 'percentage' as const 
    },
    { 
      label: 'Ratio de deuda', 
      current: summary?.debt_ratio || 0, 
      format: 'percentage' as const 
    },
    { 
      label: 'Transacciones', 
      current: monthlyTransactions.length, 
      format: 'number' as const 
    },
  ], [summary, monthlyTransactions.length]);

  const handlePreviousMonth = () => {
    setTransitionDirection('right');
    setIsTransitioning(true);
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    setTransitionDirection('left');
    setIsTransitioning(true);
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const recommendations = summary 
    ? generateRecommendations(summary, hasActiveGoals)
    : [];

  return (
    <AppLayout>
      <DailyTipProvider>
      <div className="space-y-4 sm:space-y-6">
        <PwaInstallBanner />
        <UserInfoReminder />

        {/* Header with gradient background */}
        <div className="dashboard-header rounded-xl p-4 sm:p-6 -mx-2 sm:-mx-0">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Panel Principal</h1>
                <p className="text-muted-foreground text-sm sm:text-base mt-0.5">
                  Tu resumen financiero mensual
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <PwaInstallNavButton
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg bg-card border shadow-sm"
                  iconClassName="h-4 w-4 text-primary"
                />
                {/* Bank statement import */}
                <BankStatementImport variant="compact" />

                {/* Month selector - improved styling */}
                <div className="flex items-center gap-1 bg-card rounded-xl border shadow-sm p-1">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg touch-manipulation hover:bg-muted" 
                    onClick={handlePreviousMonth}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1">
                    <Calendar className="h-3.5 w-3.5 text-primary hidden sm:block" />
                    <span className="font-semibold text-xs sm:text-sm min-w-[80px] sm:min-w-[110px] text-center">
                      {monthNames[selectedMonth - 1].slice(0, 3)} {selectedYear}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg touch-manipulation hover:bg-muted" 
                    onClick={handleNextMonth}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Quick stats bar */}
            <QuickStatsBar stats={quickStats} />
          </div>
        </div>

        {/* Animated content wrapper */}
        <div 
          ref={contentRef}
          key={`${selectedYear}-${selectedMonth}`}
          className={`space-y-4 sm:space-y-6 transition-all duration-300 ease-out ${
            isTransitioning 
              ? `opacity-0 ${transitionDirection === 'left' ? '-translate-x-4' : 'translate-x-4'}` 
              : 'opacity-100 translate-x-0'
          }`}
        >
          {/* Budget Alerts */}
          {hasAlerts && (
            <BudgetAlertBanner 
              warningAlerts={warningAlerts} 
              exceededAlerts={exceededAlerts} 
              currency={currency} 
            />
          )}

          {/* FOCO Score */}
          <FocoScoreCard
            showDetailBreakdown={isPremium} 
            score={summary?.foco_score || 0} 
            componentData={summary ? {
              savingsRate: summary.savings_rate,
              expenseRatio: summary.total_income > 0 
                ? (summary.total_fixed_expenses + summary.total_variable_expenses + summary.total_debt_payments) / summary.total_income 
                : 0,
              activeGoalsWithProgress,
              debtRatio: summary.debt_ratio,
              cashFlow: summary.real_cash_flow,
              totalIncome: summary.total_income,
            } : undefined}
          />
          {!isPremium && (
            <p className="text-sm text-muted-foreground -mt-2">
              <Link to="/upgrade" className="text-primary underline font-medium">
                Ver desglose completo del FOCO Score con Premium
              </Link>
            </p>
          )}

        {/* Metrics Grid - improved responsive layout */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4">
          <MetricCard
            title="Ingresos"
            value={formatCurrency(summary?.total_income || 0, currency)}
            icon={<TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />}
            trend={calculateTrend(summary?.total_income || 0, prevSummary?.total_income, true)}
            delay={0}
          />
          <MetricCard
            title="Gastos fijos"
            value={formatCurrency(summary?.total_fixed_expenses || 0, currency)}
            subtitle={summary?.total_income ? `${formatPercentage(summary.fixed_expense_ratio)} del ingreso` : undefined}
            icon={<Wallet className="h-4 w-4 sm:h-5 sm:w-5" />}
            trend={calculateTrend(summary?.total_fixed_expenses || 0, prevSummary?.total_fixed_expenses, false)}
            delay={50}
          />
          <MetricCard
            title="Gastos variables"
            value={formatCurrency(summary?.total_variable_expenses || 0, currency)}
            subtitle={summary?.total_income ? `${formatPercentage(summary.variable_expense_ratio)} del ingreso` : undefined}
            icon={<CreditCard className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getCombinedExpensesTrafficLight(summary.fixed_expense_ratio, summary.variable_expense_ratio) : undefined}
            trend={calculateTrend(summary?.total_variable_expenses || 0, prevSummary?.total_variable_expenses, false)}
            delay={100}
          />
          <MetricCard
            title="Ahorro + Inversión"
            value={formatCurrency((summary?.total_savings || 0) + (summary?.total_investments || 0), currency)}
            subtitle={summary?.total_income ? `${formatPercentage(summary.savings_rate)} del ingreso` : undefined}
            icon={<PiggyBank className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getSavingsTrafficLight(summary.savings_rate) : undefined}
            trend={calculateTrend(
              (summary?.total_savings || 0) + (summary?.total_investments || 0),
              (prevSummary?.total_savings || 0) + (prevSummary?.total_investments || 0),
              true
            )}
            delay={150}
          />
          <MetricCard
            title="Pago de deudas"
            value={formatCurrency(summary?.total_debt_payments || 0, currency)}
            subtitle={summary?.total_income ? `${formatPercentage(summary.debt_ratio)} del ingreso` : undefined}
            icon={<TrendingDown className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getDebtTrafficLight(summary.debt_ratio) : undefined}
            trend={calculateTrend(summary?.total_debt_payments || 0, prevSummary?.total_debt_payments, true)}
            delay={200}
          />
          <MetricCard
            title="Flujo real"
            value={formatCurrency(summary?.real_cash_flow || 0, currency)}
            subtitle={summary?.real_cash_flow && summary.real_cash_flow > 0 ? 'Superávit' : summary?.real_cash_flow && summary.real_cash_flow < 0 ? 'Déficit' : undefined}
            icon={<ArrowUpRight className="h-4 w-4 sm:h-5 sm:w-5" />}
            trafficLight={summary ? getCashFlowTrafficLight(summary.real_cash_flow) : undefined}
            trend={calculateTrend(summary?.real_cash_flow || 0, prevSummary?.real_cash_flow, true)}
            delay={250}
          />
        </div>

        {/* Budget Rule 50/30/20 Guide */}
        {summary && summary.total_income > 0 && (
          <BudgetRuleGuide
            totalIncome={summary.total_income}
            fixedExpenses={summary.total_fixed_expenses}
            variableExpenses={summary.total_variable_expenses}
            savings={summary.total_savings}
            investments={summary.total_investments}
            debtPayments={summary.total_debt_payments}
            currency={currency}
          />
        )}

        {/* Charts and Activity - improved layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Charts section */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <div className="chart-container p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Ingresos vs Salidas</h3>
              <Suspense fallback={<ChartSkeleton />}>
                <IncomeVsExpensesChart
                  income={summary?.total_income || 0}
                  fixedExpenses={summary?.total_fixed_expenses || 0}
                  variableExpenses={summary?.total_variable_expenses || 0}
                  savings={summary?.total_savings || 0}
                  investments={summary?.total_investments || 0}
                  debtPayments={summary?.total_debt_payments || 0}
                />
              </Suspense>
            </div>

            <div className="chart-container p-4 sm:p-6">
              <h3 className="text-base sm:text-lg font-semibold mb-4">Distribución de salidas</h3>
              <Suspense fallback={<ChartSkeleton />}>
                <DistributionChart
                  fixedExpenses={summary?.total_fixed_expenses || 0}
                  variableExpenses={summary?.total_variable_expenses || 0}
                  savings={summary?.total_savings || 0}
                  investments={summary?.total_investments || 0}
                  debtPayments={summary?.total_debt_payments || 0}
                />
              </Suspense>
            </div>
          </div>

          {/* Recent Activity sidebar */}
          <div className="lg:col-span-1">
            <RecentActivity 
              transactions={monthlyTransactions} 
              currency={currency} 
            />
          </div>
        </div>

        {/* Budget Progress */}
        {alerts.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Presupuestos del mes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {alerts.map(alert => (
                <BudgetProgressCard key={alert.category} alert={alert} currency={currency} />
              ))}
            </div>
          </div>
        )}

          {/* Recommendations */}
          <RecommendationsCard recommendations={recommendations} />
        </div>
      </div>
      </DailyTipProvider>
    </AppLayout>
  );
}
